// Centralized outdoor-board score helpers.
//
// DB stores scores on a 0..100 scale (enforced by CHECK constraints on
// outdoor_boards added in Phase 1, and by LEAST(100, …) clamps in the
// compute_single_board_scores RPC). Anything consuming score_* columns
// should go through here.

export type Score100 = number; // 0..100

export type Tier = 'A' | 'B' | 'C' | 'X';

const A_THRESHOLD = 75;
const B_THRESHOLD = 50;
const C_THRESHOLD = 25;

export function tierFromScore(score: Score100 | null | undefined): Tier {
  if (score == null) return 'X';
  const s = Number(score);
  if (!Number.isFinite(s)) return 'X';
  if (s >= A_THRESHOLD) return 'A';
  if (s >= B_THRESHOLD) return 'B';
  if (s >= C_THRESHOLD) return 'C';
  return 'X';
}

export function tierColor(score: Score100 | null | undefined): string {
  const t = tierFromScore(score);
  if (t === 'A') return '#2ecc71';
  if (t === 'B') return '#4a9eff';
  if (t === 'C') return '#f39c12';
  return '#888';
}

/** Map score 0..100 → map marker radius in px (5..10). */
export function scoreToRadiusPx(score: Score100 | null | undefined): number {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  return 5 + (s / 100) * 5;
}

/** Pass-through for progress-bar width %. Safe against NULL and out-of-range. */
export function scoreToBarPct(score: Score100 | null | undefined): number {
  return Math.max(0, Math.min(100, Number(score) || 0));
}

/** Display-ready integer score "73" (no decimals, no scaling). */
export function formatScore(score: Score100 | null | undefined): string {
  if (score == null) return '—';
  const s = Number(score);
  if (!Number.isFinite(s)) return '—';
  return s.toFixed(0);
}
